import { EditOutlined, PhoneOutlined } from '@ant-design/icons';
import { Button, Input, List, Tag, Tooltip, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { getCallHistory, updateCallNotes } from '../../api/telephony/cloudtalk.js';
import { formatDateTime } from '../../library/utilities/intl.js';
import { formatDuration } from './call-summary.jsx';

const { TextArea } = Input;
const PAGE_SIZE = 10;
const PROMPT_WINDOW_MS = 15 * 60 * 1000;
// Steady poll so webhook/cron completions appear on their own, no matter which
// path ended the call (ERP button, widget, desktop app). Cheap indexed query,
// paused while the tab is hidden.
const POLL_MS = 30000;

const directionColor = (direction) => (String(direction || '').toLowerCase() === 'inbound' ? 'blue' : 'green');

const CallHistory = ({ contact_id, customer_id, project_id, refreshKey, compact = false }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [editingId, setEditingId] = useState(null);
    const [notesDraft, setNotesDraft] = useState('');
    // _pollTick is write-only by design: bumping it retriggers the fetch.
    const [_pollTick, setPollTick] = useState(0);
    // Rows already seen: anything appearing later is a new/updated call.
    // Initialized on first fetch so mounting never prompts for old calls.
    const seenIdsRef = useRef(null);
    const openedPromptRef = useRef(new Set());

    useEffect(() => {
        seenIdsRef.current = null;
        setPage(1);
    }, [contact_id, customer_id, project_id, refreshKey]);

    useEffect(() => {
        const id = setInterval(() => {
            if (!document.hidden) setPollTick((t) => t + 1);
        }, POLL_MS);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const fetchHistory = async () => {
            setLoading(true);
            try {
                const { rows, pagination } = await getCallHistory({
                    contact_id,
                    customer_id,
                    project_id,
                    page,
                    pageSize: PAGE_SIZE,
                });
                if (cancelled) return;
                setLogs(rows);
                setTotal(pagination?.total || 0);
                // After-call notes prompt: any row that newly arrived (or
                // newly completed) since the last fetch, recent and still
                // noteless, gets its editor opened — once per call. First
                // fetch only marks rows as seen, so mounting never prompts.
                const prevSeen = seenIdsRef.current;
                const currentIds = new Set((rows || []).map((r) => r.id));
                if (prevSeen === null) {
                    seenIdsRef.current = currentIds;
                } else {
                    seenIdsRef.current = new Set([...prevSeen, ...currentIds]);
                    if (page === 1) {
                        const fresh = (rows || []).find((r) =>
                            !prevSeen.has(r.id) &&
                            !r.notes && r.started_at &&
                            Date.now() - new Date(r.started_at).getTime() < PROMPT_WINDOW_MS &&
                            !openedPromptRef.current.has(r.id));
                        if (fresh) {
                            openedPromptRef.current.add(fresh.id);
                            setEditingId(fresh.id);
                            setNotesDraft('');
                        }
                    }
                }
            } catch (e) {
                if (!cancelled) message.error(e.message || 'Could not load call history');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchHistory();
        return () => {
            cancelled = true;
        };
    }, [contact_id, customer_id, project_id, page, refreshKey, _pollTick]);

    const startEdit = (log) => {
        setEditingId(log.id);
        setNotesDraft(log.notes || '');
    };

    const saveNotes = async (log) => {
        try {
            const updated = await updateCallNotes(log.id, notesDraft);
            setLogs((prev) => prev.map((row) => (row.id === log.id ? { ...row, notes: updated.notes } : row)));
            setEditingId(null);
            message.success('Notes saved');
        } catch (e) {
            message.error(e.message || 'Could not save notes');
        }
    };

    return (
        <List
            loading={loading}
            dataSource={logs}
            locale={{ emptyText: 'No calls logged yet' }}
            pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (t) => `${t} call${t === 1 ? '' : 's'}`,
                onChange: (p) => setPage(p),
            }}
            renderItem={(log) => (
                <List.Item
                    key={log.id}
                    actions={[
                        <Tooltip title="Edit notes" key="notes">
                            <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(log)} />
                        </Tooltip>,
                    ]}
                >
                    <List.Item.Meta
                        avatar={<PhoneOutlined style={{ fontSize: compact ? 14 : 18, marginTop: 4 }} />}
                        title={
                            <span>
                                <Tag color={directionColor(log.direction)}>{String(log.direction || '').toLowerCase() === 'inbound' ? 'Incoming' : 'Outgoing'}</Tag>
                                {log.to_number && <span style={{ marginRight: 8 }}>{log.to_number}</span>}
                                <span style={{ color: '#888' }}>{formatDuration(log.duration_sec)} · {log.status}</span>
                            </span>
                        }
                        description={
                            <div>
                                <div style={{ fontSize: 12, color: '#888' }}>
                                    {log.started_at ? formatDateTime(log.started_at) : ''}
                                    {log.erp_user?.name ? ` · Called by: ${log.erp_user.name}` : ''}
                                    {log.caller_id_number ? ` · From: ${log.caller_id_number}` : ''}
                                    {log.project_id ? ` · Project: ${log.project_id}` : ''}
                                    {log.recording_url && (
                                        <> · <a href={log.recording_url} target="_blank" rel="noreferrer">Recording</a></>
                                    )}
                                </div>
                                {editingId === log.id ? (
                                    <div style={{ marginTop: 8 }}>
                                        <TextArea rows={3} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} placeholder="Call notes and customer discussion…" style={{ borderRadius: 4 }} />
                                        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                                            <Button size="small" type="primary" style={{ backgroundColor: '#10b981', borderColor: '#10b981' }} onClick={() => saveNotes(log)}>Save Notes</Button>
                                            <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
                                        </div>
                                    </div>
                                ) : (
                                    log.notes ? (
                                        <div style={{ marginTop: 8, padding: '8px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb', whiteSpace: 'pre-wrap', color: '#1f2937', fontSize: 13 }}>
                                            <span style={{ fontWeight: 600, color: '#374151', marginRight: 6 }}>Notes:</span>
                                            {log.notes}
                                        </div>
                                    ) : null
                                )}
                            </div>
                        }
                    />
                </List.Item>
            )}
        />
    );
};

CallHistory.propTypes = {
    contact_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    customer_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    project_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    compact: PropTypes.bool,
};

export default CallHistory;
